import {
  getConversation,
  getConversationIdFromPathName,
} from 'conversations/modules/conversation/selectors'
import { getTeamsById } from 'conversations/modules/teams/selectors'
import { getFilteredConversationSections } from 'conversations/modules/user/sections/selectors'
import { getCurrentInboxId, getCurrentUserId } from 'conversations/modules/user/selectors'
import { createConditionsArray, evaluateCondition } from 'conversations/utils/conditionsUtil'
import { decrementCount, incrementCount } from 'shared/store/actions/conversation'
import ConversationType from 'shared/typings/conversations/Conversation'
import {
  ConversationSidebarFilterCondition,
  ConversationSidebarSectionConfig,
} from 'shared/typings/conversationsSidebar/Filters'

import { access, GlobalStore as store } from '@driftt/traffic-control'

import { ActionType } from 'typesafe-actions'

const getSectionsForConversation = (conversation?: ConversationType): string[] => {
  if (!conversation) {
    return []
  }
  const customSidebarSections = getFilteredConversationSections(store.getState())
  const currentUserId = getCurrentUserId(store.getState())
  const teams = getTeamsById(store.getState())
  return customSidebarSections.reduce(
    (acc: string[], sectionConfig: ConversationSidebarSectionConfig) => {
      switch (sectionConfig.type) {
        case 'my-convos':
          conversation.participantIds.includes(currentUserId ?? 0) && acc.push(sectionConfig.id)
          break
        case 'no-participants':
          conversation.participantIds.length === 0 && acc.push(sectionConfig.id)
          break
        case 'all-convos':
          acc.push(sectionConfig.id)
          break
        case 'compound':
        case null: {
          const conditions = createConditionsArray(sectionConfig.condition)
          switch (sectionConfig.condition.operator) {
            case 'or': {
              if (
                conditions?.some((condition: ConversationSidebarFilterCondition) =>
                  evaluateCondition(conversation, teams, condition)
                )
              ) {
                acc.push(sectionConfig.id)
              }
              break
            }
            default: {
              if (
                conditions?.every((condition: ConversationSidebarFilterCondition) =>
                  evaluateCondition(conversation, teams, condition)
                )
              ) {
                acc.push(sectionConfig.id)
              }
              break
            }
          }
        }
      }

      return acc
    },
    []
  )
}

export const isConversationInCurrentInbox = (conversation?: ConversationType): boolean => {
  const currentInboxId = getCurrentInboxId(store.getState())
  return !currentInboxId || currentInboxId === conversation?.inboxId
}

export const isCurrentConversationId = (conversationId: number): boolean =>
  conversationId === getConversationIdFromPathName()

export const isConversationInSomeSection = (conversation?: ConversationType): boolean => {
  if (!conversation) {
    return false
  }

  const oldConversation = getConversation(conversation.id)(store.getState())
  const newSections = getSectionsForConversation(conversation)
  const oldSections = getSectionsForConversation(oldConversation)

  return newSections.length > 0 || oldSections.length > 0
}

export const updateCounts = (conversation: ConversationType): any => {
  const oldConversation = getConversation(conversation.id)(store.getState())
  const oldSections = getSectionsForConversation(oldConversation)
  const newSections = getSectionsForConversation(conversation)
  const actions: ActionType<any>[] = []

  for (const sectionUUID of newSections) {
    if (oldSections.indexOf(sectionUUID) === -1) {
      actions.push(incrementCount({ sectionUUID, status: conversation.status }))
      actions.push(incrementCount({ sectionUUID, status: 'all' }))
    }
  }

  if (!oldConversation) {
    return actions
  }

  for (const sectionUUID of oldSections) {
    if (newSections.indexOf(sectionUUID) === -1) {
      actions.push(decrementCount({ sectionUUID, status: oldConversation.status }))
      actions.push(decrementCount({ sectionUUID, status: 'all' }))
    } else if (oldConversation && oldConversation.status !== conversation.status) {
      actions.push(decrementCount({ sectionUUID, status: oldConversation.status }))
      actions.push(incrementCount({ sectionUUID, status: conversation.status }))
    }
  }
  return actions
}

export const hasOnlyMyConvosAccess = () => {
  if (!access.isRbacUser()) {
    return (
      !access.isAdmin() &&
      !access.hasScopes('convo-ALL') &&
      !access.hasScopes('convo-WITH_NO_PARTICIPANTS')
    )
  }

  if (access.hasPermissionForWorkspace('convo_view_all', 'READ', 'global')) {
    return false
  }

  if (access.hasFeatures([{ featureName: 'convo_view_no_participants', accessLevel: 'READ' }])) {
    return false
  }

  if (access.hasFeatures([{ featureName: 'convo_view_only_mine', accessLevel: 'READ' }])) {
    return true
  }
}
